import React, { useMemo } from 'react';
import { Box, TextField } from '@mui/material';
import InlineAlert from '../../../shared/components/InlineAlert';
import type { PromptTypeType } from '../../../types/backend';
import { detectTemplateVariables } from '../utils/promptUtils';

interface PromptContentEditorProps {
  content: string;
  onChange: (content: string) => void;
  contentError: string | null;
  viewingVersion: string | null;
  viewingContent: string;
  versionLoading: boolean;
  promptType: PromptTypeType;
}

const PromptContentEditor: React.FC<PromptContentEditorProps> = ({
  content,
  onChange,
  contentError,
  viewingVersion,
  viewingContent,
  versionLoading,
  promptType,
}) => {
  const isViewingHistory = viewingVersion !== null;
  const isEnrichment = promptType === 'enrichment';
  const missingEventPlaceholder = isEnrichment && content.length > 0 && !content.includes('{event}');

  const detectedVariables = useMemo(() => {
    return detectTemplateVariables(content);
  }, [content]);

  const variablesHint = detectedVariables.length > 0
    ? `Template variables: ${detectedVariables.join(', ')}`
    : undefined;

  return (
    <Box sx={{ px: 3, pt: 2 }}>
      {isViewingHistory ? (
        <>
          <InlineAlert severity="info" sx={{ mb: 1 }}>
            Viewing published v{viewingVersion} (read-only). Select &quot;Draft&quot; to resume editing.
          </InlineAlert>
          <TextField
            value={versionLoading ? 'Loading...' : viewingContent}
            multiline
            fullWidth
            minRows={12}
            inputProps={{
              readOnly: true,
            }}
            sx={{ '& .MuiInputBase-root': { bgcolor: 'action.hover' }, '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.875rem' } }}
          />
        </>
      ) : (
        <>
          {isEnrichment && (
            <InlineAlert severity={missingEventPlaceholder ? 'warning' : 'info'} sx={{ mb: 1 }}>
              Enrichment prompts must include the <code>{'{event}'}</code> placeholder — it is
              replaced with the triggering event payload before the model runs.
            </InlineAlert>
          )}
          <TextField
            value={content}
            onChange={(e) => onChange(e.target.value)}
            error={!!contentError}
            helperText={contentError || variablesHint}
            placeholder={"# Prompt Instructions\n\n## Objective\n...\n\n## Steps\n1. ...\n2. ..."}
            multiline
            fullWidth
            minRows={12}
            sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.875rem' } }}
          />
        </>
      )}
    </Box>
  );
};

export default PromptContentEditor;
